import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Text, Flex, Button } from '@chakra-ui/react';
import { UserPlus } from 'lucide-react';
import { NavBar, Toast } from '@/components/ui';
import { OrgContextSwitcher } from '@/components/ui/OrgContextSwitcher/OrgContextSwitcher';
import { useToast } from '@/hooks/useToast';
import { useUser } from '@/constants/UserContext';
import { useVolunteersPage, type VolunteersTab } from './useVolunteersPage';
import { VolunteersHeader } from './VolunteersHeader';
import { VolunteersWaitingList } from './VolunteersWaitingList';
import { VolunteersStats } from './VolunteersStats.tsx';
import { VolunteersHourRequestsModal } from './VolunteersHourRequestsModal';
import { TasksModal } from './TasksModal';
import { VolunteersManageModal } from './VolunteersManageModal';
import { fetchOrgMemberManageContext } from './fetchOrgMemberManageContext';
import type { OrgMemberManagePayload } from './fetchOrgMemberManageContext';
import type { OrganizationAcceptedMember } from '@/hooks/useOrganizationAcceptedMembers';
import { VolunteersDepartmentsView } from './VolunteersDepartmentsView';
import { VolunteersShiftsView } from './VolunteersShiftsView';
import { useActAsEventOrg } from './useActAsEventOrg';
import { ExportVolunteersButton } from './components/ExportVolunteersButton';
import { useOpenDirectChat } from '@/hooks/useOpenDirectChat';
import {
  HourRequestsTasksBar,
  EventSelector,
  VolunteersTabs,
  VolunteersSubToolbar,
  PendingMembershipRequestsSection,
  OrganizationMembersSection,
} from './components';
import { buildEventInviteLink } from '@/utils/buildEventInviteLink';
import { PullToRefresh } from '@/components/common/PullToRefresh';

const orgDashboardTabs = [
  { id: 'events', label: 'EVENTS' },
  { id: 'members', label: 'MEMBERS' },
] as const;

type OrgDashboardTab = (typeof orgDashboardTabs)[number]['id'];

type MembersSubTab = 'requests' | 'members';

const tabs: { id: VolunteersTab; label: string }[] = [
  { id: 'waiting', label: 'Waiting list' },
  { id: 'volunteers', label: 'Volunteers' },
  { id: 'shifts', label: 'Shifts' },
  { id: 'stats', label: 'Stats' },
];

const DESKTOP_BREAKPOINT = 1024;

export function VolunteersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkOpenHandled = useRef<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const fn = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  const { showToast, hideToast, toast } = useToast();
  const { user, activeAdminOrgId } = useUser();
  const isOrganization = user?.role === 'organization';
  const isOrgView = isOrganization || Boolean(activeAdminOrgId);
  const effectiveOrgId = isOrganization ? user?.id : (activeAdminOrgId ?? undefined);

  const { openChat } = useOpenDirectChat();

  /** Open (or reuse) the private thread with this volunteer and jump to it. */
  const handleMessageVolunteer = async (volunteer: { volunteer_id?: string | null }) => {
    const volunteerId = volunteer.volunteer_id;
    const eventId = vm.selectedEventId;
    if (!volunteerId || !eventId || !effectiveOrgId) return;
    const result = await openChat(eventId, volunteerId, effectiveOrgId);
    if (!result.ok) {
      showToast(result.message, 'error');
      return;
    }
    navigate(`/communication?chat=${encodeURIComponent(result.chatId)}`);
  };

  const [orgDashboardTab, setOrgDashboardTab] = useState<OrgDashboardTab>('events');
  const [membersSubTab, setMembersSubTab] = useState<MembersSubTab>('requests');
  const [orgMemberManage, setOrgMemberManage] = useState<OrgMemberManagePayload | null>(null);
  const [membersRefreshSig, setMembersRefreshSig] = useState(0);
  const [loadingOrgMemberMembershipId, setLoadingOrgMemberMembershipId] = useState<string | null>(null);

  // A notification link names an event; if it belongs to an org this admin runs, act as it.
  useActAsEventOrg(searchParams.get('eventId'));

  // Reset to events tab when org context changes
  useEffect(() => {
    setOrgDashboardTab('events');
    setMembersSubTab('requests');
  }, [activeAdminOrgId]);

  useEffect(() => {
    if (orgDashboardTab !== 'members') {
      setMembersSubTab('requests');
    }
  }, [orgDashboardTab]);

  const vm = useVolunteersPage({
    onHourRequestError: (msg) => showToast(msg, 'error'),
    onHourRequestSuccess: () => showToast('Hours added', 'success'),
    onWaitingListError: (msg) => showToast(msg, 'error'),
    onWaitingListWarning: (msg) => showToast(msg, 'info'),
    onShiftError: (msg) => showToast(msg, 'error'),
    overrideOrgId: effectiveOrgId,
  });
  const hasEvents = vm.events.length > 0 && !vm.eventsLoading;
  const showEventsDashboard = !isOrgView || orgDashboardTab === 'events';

  useEffect(() => {
    const orgTab = searchParams.get('orgTab');
    if (orgTab === 'members' || orgTab === 'events') {
      setOrgDashboardTab(orgTab);
    }
    const membersTab = searchParams.get('membersTab');
    if (membersTab === 'requests' || membersTab === 'members') {
      setMembersSubTab(membersTab);
    }
  }, [searchParams]);

  // Destructured so the effect below can depend on the functions rather than all of `vm`.
  const { fetchHourRequests, setShowHourRequestsModal, setShowTasksModal } = vm;
  useEffect(() => {
    if (!isOrgView) return;
    const open = searchParams.get('open');
    if (!open || (open !== 'hourRequests' && open !== 'tasks')) return;

    const urlEventId = searchParams.get('eventId');
    if (open === 'hourRequests') {
      if (!vm.selectedEventId) return;
      if (urlEventId && vm.selectedEventId !== urlEventId) return;
      const key = `hourRequests:${vm.selectedEventId}`;
      if (deepLinkOpenHandled.current === key) return;
      deepLinkOpenHandled.current = key;
      void fetchHourRequests();
      setShowHourRequestsModal(true);
    } else if (open === 'tasks') {
      if (!vm.selectedEventId) return;
      if (urlEventId && vm.selectedEventId !== urlEventId) return;
      const tasksTab = searchParams.get('tasksTab') ?? '';
      const key = `tasks:${vm.selectedEventId}:${tasksTab}`;
      if (deepLinkOpenHandled.current === key) return;
      deepLinkOpenHandled.current = key;
      setShowTasksModal(true);
    }
  }, [
    isOrgView,
    searchParams,
    vm.selectedEventId,
    fetchHourRequests,
    setShowHourRequestsModal,
    setShowTasksModal,
  ]);

  // Drop ?open= on close, or tapping the same notification again navigates to the URL already
  // showing: searchParams does not change, the effect above never re-runs, and nothing opens.
  const handleHourRequestsModalClose = useCallback(() => {
    vm.setShowHourRequestsModal(false);
    if (searchParams.get('open') === 'hourRequests') {
      const p = new URLSearchParams(searchParams);
      p.delete('open');
      const qs = p.toString();
      navigate(qs ? `/volunteers?${qs}` : '/volunteers', { replace: true });
      deepLinkOpenHandled.current = null;
    }
  }, [vm, searchParams, navigate]);

  const tasksModalInitialOrgTab =
    searchParams.get('tasksTab') === 'complete' ? ('complete' as const) : undefined;

  const handleTasksModalClose = useCallback(() => {
    vm.setShowTasksModal(false);
    const openParam = searchParams.get('open');
    if (openParam === 'tasks' || searchParams.get('tasksTab')) {
      const p = new URLSearchParams(searchParams);
      p.delete('open');
      p.delete('tasksTab');
      const qs = p.toString();
      navigate(qs ? `/volunteers?${qs}` : '/volunteers', { replace: true });
      deepLinkOpenHandled.current = null;
    }
  }, [vm, searchParams, navigate]);

  const openEventManageVolunteer = useCallback(
    (v: Parameters<typeof vm.setManageVolunteer>[0]) => {
      setOrgMemberManage(null);
      vm.setManageVolunteer(v);
    },
    [vm]
  );

  const handleManageOrgMember = useCallback(
    async (member: OrganizationAcceptedMember) => {
      if (!effectiveOrgId) return;
      setLoadingOrgMemberMembershipId(member.membershipId);
      vm.setManageVolunteer(null);
      try {
        const ctx = await fetchOrgMemberManageContext(effectiveOrgId, member.volunteerId, member.membershipId);
        if (ctx) setOrgMemberManage(ctx);
      } finally {
        setLoadingOrgMemberMembershipId(null);
      }
    },
    [effectiveOrgId, vm]
  );

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      vm.refetchEvents(),
      vm.refetch(),
      vm.fetchVolunteerApplications(),
      vm.fetchHourRequests(),
      vm.fetchTasksCount(),
      vm.fetchEventStats(),
    ]);
    // Refresh the org "members" tab sections (members list + pending requests).
    setMembersRefreshSig((n) => n + 1);
  }, [vm]);

  return (
    <Box
      minH="100vh"
      pb={isDesktop ? '24px' : 'var(--mobile-bottom-safe)'}
      pl={isDesktop ? 'var(--sidebar-width)' : 0}
      transition="padding-left 0.3s ease-in-out"
    >
      <PullToRefresh onRefresh={handleRefresh}>
      <Box
        maxW={{ base: '100%', md: '920px', xl: '1040px' }}
        mx="auto"
        px={{ base: '5', md: '6' }}
        py="5"
      >
        <VolunteersHeader />

        {/* Org context switcher — shown to volunteers with admin org access */}
        <OrgContextSwitcher />

        {isOrgView && (
          <VolunteersTabs
            tabs={[...orgDashboardTabs]}
            activeTab={orgDashboardTab}
            onChange={(id) => setOrgDashboardTab(id as OrgDashboardTab)}
          />
        )}

        {isOrgView && orgDashboardTab === 'members' && effectiveOrgId && (
          <>
            <Flex gap="3" mb="4" role="tablist" aria-label="Membership views">
              <Button
                type="button"
                role="tab"
                aria-selected={membersSubTab === 'requests'}
                flex="1"
                h="12"
                borderRadius="xl"
                fontWeight="semibold"
                fontSize="sm"
                border="2px solid"
                transition="background 0.2s, color 0.2s, border-color 0.2s"
                bg={membersSubTab === 'requests' ? '#1F2932' : 'white'}
                color={membersSubTab === 'requests' ? 'white' : 'volyx.textSecondary'}
                borderColor={membersSubTab === 'requests' ? '#1F2932' : 'volyx.mediumGray'}
                _hover={{
                  bg: membersSubTab === 'requests' ? '#171e28' : 'volyx.lightGray',
                  borderColor: membersSubTab === 'requests' ? '#171e28' : 'volyx.darkGray',
                }}
                onClick={() => setMembersSubTab('requests')}
              >
                Requests
              </Button>
              <Button
                type="button"
                role="tab"
                aria-selected={membersSubTab === 'members'}
                flex="1"
                h="12"
                borderRadius="xl"
                fontWeight="semibold"
                fontSize="sm"
                border="2px solid"
                transition="background 0.2s, color 0.2s, border-color 0.2s"
                bg={membersSubTab === 'members' ? '#1F2932' : 'white'}
                color={membersSubTab === 'members' ? 'white' : 'volyx.textSecondary'}
                borderColor={membersSubTab === 'members' ? '#1F2932' : 'volyx.mediumGray'}
                _hover={{
                  bg: membersSubTab === 'members' ? '#171e28' : 'volyx.lightGray',
                  borderColor: membersSubTab === 'members' ? '#171e28' : 'volyx.darkGray',
                }}
                onClick={() => setMembersSubTab('members')}
              >
                Members
              </Button>
            </Flex>
            {membersSubTab === 'requests' && (
              <PendingMembershipRequestsSection
                organizationUserId={effectiveOrgId}
                refreshSignal={membersRefreshSig}
              />
            )}
            {membersSubTab === 'members' && (
              <OrganizationMembersSection
                organizationUserId={effectiveOrgId}
                refreshSignal={membersRefreshSig}
                loadingMembershipId={loadingOrgMemberMembershipId}
                onManageMember={handleManageOrgMember}
              />
            )}
          </>
        )}

        {/* Event selector + Invite button */}
        {showEventsDashboard && hasEvents && (
          <Flex gap="3" alignItems="center" mb="3">
            <Box flex="1" mb="0">
              <EventSelector
                events={vm.events}
                selectedEventId={vm.selectedEventId}
                onSelect={(id) => vm.setSelectedEventId(id)}
                loading={vm.eventsLoading}
                noMargin
              />
            </Box>
            <Button
              flexShrink={0}
              bg="volyx.bg"
              border="1.5px solid"
              borderColor="volyx.primary"
              color="volyx.primary"
              borderRadius="xl"
              w="48px"
              h="48px"
              p="0"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 8px 20px rgba(0,0,0,0.08)"
              cursor="pointer"
              aria-label="Copy event invite link"
              _hover={{ bg: 'volyx.lightGray', boxShadow: '0 12px 28px rgba(0,0,0,0.12)' }}
              onClick={() => {
                if (vm.selectedEventId) {
                  const link = buildEventInviteLink(vm.selectedEventId);
                  navigator.clipboard.writeText(link).then(
                    () => showToast('Invite link copied', 'success'),
                    () => showToast('Failed to copy link', 'error'),
                  );
                }
              }}
            >
              <UserPlus size={20} />
            </Button>
            {isOrgView && (
              <ExportVolunteersButton
                eventId={vm.selectedEventId}
                eventName={vm.selectedEvent?.name}
                onToast={showToast}
              />
            )}
          </Flex>
        )}

        {showEventsDashboard && !hasEvents && !vm.eventsLoading && (
          <Box bg="volyx.bg" borderRadius="xl" boxShadow="0 1px 3px rgba(0,0,0,0.08)" p="4" mb="3" textAlign="center">
            <Text fontSize="xs" color="volyx.textSecondary">You don't have any active events yet.</Text>
          </Box>
        )}

        {/* Hour Requests + Tasks buttons */}
        {showEventsDashboard && hasEvents && (
          <HourRequestsTasksBar
            hourRequestsCount={vm.hourRequestsCount}
            tasksCount={vm.tasksCount}
            onOpenHourRequests={() => {
              vm.fetchHourRequests();
              vm.setShowHourRequestsModal(true);
            }}
            onOpenTasks={() => {
              if (!isOrgView) {
                navigate('/volunteer-tasks');
                return;
              }
              vm.setShowTasksModal(true);
            }}
          />
        )}

        {/* Main tabs */}
        {showEventsDashboard && hasEvents && (
          <VolunteersTabs
            tabs={tabs}
            activeTab={vm.activeTab}
            onChange={(id) => vm.setActiveTab(id as VolunteersTab)}
          />
        )}

        {/* Tab content: Volunteers */}
        {showEventsDashboard && vm.activeTab === 'volunteers' && hasEvents && (
          <>
            <VolunteersSubToolbar
              search={vm.search}
              onSearchChange={vm.setSearch}
              searchPlaceholder="Search volunteers by name or department..."
            />

            {vm.error && (
              <Text mt="2" fontSize="xs" color="volyx.error">
                Could not load volunteers. Please try again later.
              </Text>
            )}

            {!vm.error && vm.profilesUnavailable && (
              <Flex mt="2" align="center" gap="2" flexWrap="wrap">
                <Text fontSize="xs" color="volyx.error" m="0">
                  Could not load volunteer names. They are shown as "Volunteer" below.
                </Text>
                <Button size="xs" variant="ghost" onClick={() => vm.refetch()}>
                  Retry
                </Button>
              </Flex>
            )}

            <VolunteersDepartmentsView
              key={vm.selectedEventId ?? 'no-event'}
              volunteers={vm.volunteers}
              loading={vm.loading}
              eventDepartments={vm.selectedEvent?.departments ?? undefined}
              onManageClick={openEventManageVolunteer}
              onMessageClick={isOrgView ? (v) => void handleMessageVolunteer(v) : undefined}
              departmentColors={Object.fromEntries(vm.pieChartData.map((d) => [d.text, d.color]))}
              onUpdateVolunteerDepartment={vm.updateVolunteerDepartment}
              onDepartmentError={(msg) => showToast(msg, 'error')}
            />
          </>
        )}

        {/* Tab content: Shifts */}
        {showEventsDashboard && vm.activeTab === 'shifts' && hasEvents && (
          <VolunteersShiftsView
            key={vm.selectedEventId ?? 'no-event'}
            shifts={vm.shifts}
            roster={vm.shiftRoster}
            volunteers={vm.rawVolunteers}
            loading={vm.shiftsLoading || vm.loading}
            onAssign={vm.assignVolunteerToShift}
            onRemove={vm.removeVolunteerFromShift}
            onManageClick={openEventManageVolunteer}
          />
        )}

        {/* Tab content: Waiting list */}
        {showEventsDashboard && vm.activeTab === 'waiting' && hasEvents && (
          <VolunteersWaitingList
            applicants={vm.applicants}
            loading={vm.loadingApplicants}
            search={vm.search}
            onSearchChange={vm.setSearch}
            onAccept={vm.handleAcceptApplicant}
            onDeny={vm.handleDenyApplicant}
            onAcceptAll={vm.handleAcceptAll}
          />
        )}

        {/* Tab content: Stats */}
        {showEventsDashboard && vm.activeTab === 'stats' && hasEvents && (
          <VolunteersStats
            stats={vm.eventStats}
            chartData={vm.chartData}
            volunteersChartData={vm.volunteersChartData}
            pieChartData={vm.pieChartData}
            loading={vm.loadingStats}
          />
        )}
      </Box>
      </PullToRefresh>

      {/* Modals */}
      <VolunteersHourRequestsModal
        open={vm.showHourRequestsModal}
        onClose={handleHourRequestsModalClose}
        requests={vm.hourRequests}
        loading={vm.loadingHourRequests}
        onAccept={vm.handleAcceptHourRequest}
        onDeny={vm.handleDenyHourRequest}
      />
      {vm.selectedEventId && effectiveOrgId && (
        <TasksModal
          open={vm.showTasksModal}
          onClose={handleTasksModalClose}
          eventId={vm.selectedEventId}
          organizationId={effectiveOrgId}
          eventName={vm.selectedEvent?.name ?? ''}
          isOrganization={isOrgView}
          initialOrgTab={tasksModalInitialOrgTab}
          onTasksChanged={() => {
            void vm.fetchTasksCount();
          }}
          onError={(msg) => showToast(msg, 'error')}
          onSuccess={(msg) => showToast(msg, 'success')}
        />
      )}
      <VolunteersManageModal
        open={!!vm.manageVolunteer || !!orgMemberManage}
        onClose={() => {
          vm.setManageVolunteer(null);
          setOrgMemberManage(null);
        }}
        volunteer={orgMemberManage?.primaryVolunteer ?? vm.manageVolunteer}
        event={orgMemberManage?.primaryEvent ?? vm.selectedEvent}
        orgMemberManage={orgMemberManage}
        moveEventDepartments={vm.selectedEvent?.departments ?? []}
        departmentColors={Object.fromEntries(vm.pieChartData.map((d) => [d.text, d.color]))}
        onUpdateVolunteerDepartment={
          orgMemberManage ? undefined : vm.updateVolunteerDepartment
        }
        onSaved={() => {
          void vm.refetch();
          void vm.fetchTasksCount();
          if (orgMemberManage) setMembersRefreshSig((n) => n + 1);
        }}
        onError={(msg) => showToast(msg, 'error')}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <NavBar />
    </Box>
  );
}
